import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { getCommandGroups } from '../../../shared/constants';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '../../../shared/ui';
import { formatShortcut } from '../../../shared/utils';

type GlobalCommandBarProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void; // eslint-disable-line
};

export function GlobalCommandBar({
  open,
  onOpenChange,
}: GlobalCommandBarProps) {
  const navigate = useNavigate();

  // Global keyboard shortcut - mod+k (Cmd+K on Mac, Ctrl+K on Windows/Linux)
  useHotkeys(
    'mod+k',
    e => {
      e.preventDefault();
      onOpenChange(!open);
    },
    {
      enableOnFormTags: true,
    },
    [open]
  );

  // Get all command groups
  const commandGroups = useMemo(() => getCommandGroups(), []);

  // Handle command execution
  const handleCommandSelect = (command: {
    action: { type: string; payload: string | (() => void) };
  }) => {
    onOpenChange(false);

    if (
      command.action.type === 'navigation' &&
      typeof command.action.payload === 'string'
    ) {
      navigate({ to: command.action.payload });
    } else if (
      command.action.type === 'callback' &&
      typeof command.action.payload === 'function'
    ) {
      command.action.payload();
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder='Digite um comando ou busque...' />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>

        {commandGroups.map((group, index) => (
          <div key={group.id}>
            {index > 0 && <CommandSeparator />}
            <CommandGroup heading={group.label}>
              {group.commands.map(command => {
                const Icon = command.icon;
                return (
                  <CommandItem
                    key={command.id}
                    onSelect={() => handleCommandSelect(command)}
                  >
                    <Icon className='h-5 w-5' strokeWidth={1.6} />
                    <span>{command.label}</span>
                    {command.shortcut && (
                      <CommandShortcut>
                        {formatShortcut(command.shortcut)}
                      </CommandShortcut>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </div>
        ))}

        <CommandSeparator />
        <CommandGroup heading='Atalhos'>
          <CommandItem disabled>
            <Search className='h-5 w-5' strokeWidth={1.6} />
            <span>Abrir barra de comandos</span>
            <CommandShortcut>{formatShortcut('⌘K')}</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
